import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * Snake, as the first game on the realtime contract.
 *
 * Chosen to prove the design rather than because it is the easiest: at eight
 * steps a second it is exactly the game the existing architecture cannot carry,
 * because the room's alarm floor caps a server-driven tick at four a second. If
 * this works, Tetris (gravity at one a second) is comfortable and Flappy Bird
 * (sixty) is the same pattern with a bigger number.
 *
 * Every value here is an integer on purpose. The browser and the Durable Object
 * have to reach the same state from the same seed, and a float that rounds
 * differently on two devices is a desync that only shows up as an accusation of
 * cheating.
 */

export const NIBBLE_WIDTH = 16;
export const NIBBLE_HEIGHT = 16;
export const NIBBLE_RATE = 8;
/** Eight steps a second, so this is a little over three minutes of play. */
export const NIBBLE_MAX_STEPS = 1600;
export const NIBBLE_START_LENGTH = 4;
/** What one apple is worth. Kept whole so the score is never a float. */
export const NIBBLE_APPLE = 10;

/** Anticlockwise from east, so `(dir + 1) % 4` is a left turn. */
export const NIBBLE_DIRECTIONS = [
	{ x: 1, y: 0 },
	{ x: 0, y: -1 },
	{ x: -1, y: 0 },
	{ x: 0, y: 1 },
] as const;

export interface NibbleState extends RealtimeState {
	kind: "nibble";
	/** Head first. Cells are `y * WIDTH + x`, so the whole body is integers. */
	body: number[];
	direction: number;
	apple: number;
	/** How many apples have been eaten, which is what places the next one. */
	eaten: number;
}

/** A quarter turn, or nothing. The only thing a player can do. */
export type NibbleInput = { turn: "left" | "right" };

const cell = (x: number, y: number) => y * NIBBLE_WIDTH + x;
export const nibbleX = (at: number) => at % NIBBLE_WIDTH;
export const nibbleY = (at: number) => Math.floor(at / NIBBLE_WIDTH);

/**
 * Where the nth apple goes. Derived from the seed and the count rather than
 * stored, so the server never has to trust a client's idea of where it was.
 */
function appleFor(seed: number, eaten: number, taken: readonly number[]): number {
	const rng = createRng((seed + eaten * 2_654_435_761) >>> 0);
	// A grid this small fills up slowly, so rejection sampling is fine; the
	// bound stops a nearly-full board from spinning.
	for (let attempt = 0; attempt < 200; attempt++) {
		const at = rng.int(NIBBLE_WIDTH * NIBBLE_HEIGHT);
		if (!taken.includes(at)) return at;
	}
	// Board effectively full: put it under the head, which ends the run next
	// step. Reaching here means the player has won as thoroughly as possible.
	return taken[0];
}

export const nibble: RealtimeGame<NibbleState, NibbleInput> = {
	id: "nibble",
	rate: NIBBLE_RATE,
	maxSteps: NIBBLE_MAX_STEPS,

	create(seed) {
		const middle = Math.floor(NIBBLE_HEIGHT / 2);
		const body: number[] = [];
		// Head at the middle, tail trailing west, so the first move is forward
		// into open board whatever the player does.
		for (let i = 0; i < NIBBLE_START_LENGTH; i++) {
			body.push(cell(Math.floor(NIBBLE_WIDTH / 2) - i, middle));
		}
		return {
			kind: "nibble",
			step: 0,
			seed,
			over: false,
			score: 0,
			body,
			direction: 0,
			apple: appleFor(seed, 0, body),
			eaten: 0,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const turn = (raw as { turn?: unknown }).turn;
		return turn === "left" || turn === "right" ? { turn } : null;
	},

	step(state, input) {
		if (state.over) return state;

		const direction =
			input === null ? state.direction : (state.direction + (input.turn === "left" ? 1 : 3)) % 4;

		const heading = NIBBLE_DIRECTIONS[direction];
		const x = nibbleX(state.body[0]) + heading.x;
		const y = nibbleY(state.body[0]) + heading.y;

		// Walls end the run. No wrapping: wrapping makes the board impossible to
		// lose on, and losing is the whole shape of the game.
		if (x < 0 || y < 0 || x >= NIBBLE_WIDTH || y >= NIBBLE_HEIGHT) {
			return { ...state, step: state.step + 1, direction, over: true };
		}

		const head = cell(x, y);
		const eating = head === state.apple;
		// The tail moves out of the way in the same step, so following your own
		// tail is legal — which is what makes tight spirals playable.
		const body = eating ? state.body : state.body.slice(0, -1);
		if (body.includes(head)) {
			return { ...state, step: state.step + 1, direction, over: true };
		}

		const grown = [head, ...body];
		const eaten = state.eaten + (eating ? 1 : 0);
		return {
			...state,
			step: state.step + 1,
			direction,
			body: grown,
			eaten,
			apple: eating ? appleFor(state.seed, eaten, grown) : state.apple,
			score: eaten * NIBBLE_APPLE,
		};
	},
};

/**
 * The turn that would set off towards `target`, or null if there isn't one.
 *
 * Arrow keys and swipes name a direction; the game only has left and right.
 * Straight on needs no turn, and there is no turn that reverses — a snake that
 * could turn back would eat its own neck on the very next step, so a child
 * pressing the way they came should get nothing rather than a death.
 */
export function nibbleTurnToward(direction: number, target: number): NibbleInput | null {
	if ((direction + 1) % 4 === target) return { turn: "left" };
	if ((direction + 3) % 4 === target) return { turn: "right" };
	return null;
}

/** Which of `NIBBLE_DIRECTIONS` points this way, or -1. */
export function nibbleHeading(x: number, y: number): number {
	return NIBBLE_DIRECTIONS.findIndex((heading) => heading.x === x && heading.y === y);
}

/**
 * A survivable policy: keep going, and turn when the next cell would kill you.
 *
 * Not a good player — it does not chase the apple — but it never dies to a wall
 * and that is what the tests need to reach a natural end rather than a timeout.
 */
export function nibbleSafeTurn(state: NibbleState): NibbleInput | null {
	const fatal = (direction: number): boolean => {
		const heading = NIBBLE_DIRECTIONS[direction];
		const x = nibbleX(state.body[0]) + heading.x;
		const y = nibbleY(state.body[0]) + heading.y;
		if (x < 0 || y < 0 || x >= NIBBLE_WIDTH || y >= NIBBLE_HEIGHT) return true;
		return state.body.slice(0, -1).includes(cell(x, y));
	};

	if (!fatal(state.direction)) return null;
	if (!fatal((state.direction + 1) % 4)) return { turn: "left" };
	if (!fatal((state.direction + 3) % 4)) return { turn: "right" };
	// Boxed in. Carry straight on and take it.
	return null;
}

/**
 * Heads for the apple, and will not turn into a wall or into itself.
 *
 * This is the computer opponent, and it is also what the tests use when they
 * need a run that actually scores — a policy that never eats makes every seed
 * look identical, which hides exactly the bugs worth finding.
 */
export function nibbleChase(state: NibbleState): NibbleInput | null {
	const headX = nibbleX(state.body[0]);
	const headY = nibbleY(state.body[0]);

	const after = (direction: number) => {
		const heading = NIBBLE_DIRECTIONS[direction];
		return { x: headX + heading.x, y: headY + heading.y };
	};

	const fatal = (direction: number) => {
		const { x, y } = after(direction);
		if (x < 0 || y < 0 || x >= NIBBLE_WIDTH || y >= NIBBLE_HEIGHT) return true;
		return state.body.slice(0, -1).includes(cell(x, y));
	};

	const distance = (direction: number) => {
		const { x, y } = after(direction);
		return Math.abs(x - nibbleX(state.apple)) + Math.abs(y - nibbleY(state.apple));
	};

	// Straight on, left, right — in that order, so a tie keeps it going rather
	// than jittering on the spot.
	const options: { input: NibbleInput | null; direction: number }[] = [
		{ input: null, direction: state.direction },
		{ input: { turn: "left" }, direction: (state.direction + 1) % 4 },
		{ input: { turn: "right" }, direction: (state.direction + 3) % 4 },
	];

	const survivable = options.filter((option) => !fatal(option.direction));
	if (survivable.length === 0) return null;

	let best = survivable[0];
	for (const option of survivable) {
		if (distance(option.direction) < distance(best.direction)) best = option;
	}
	return best.input;
}
